import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AuthedImage } from "@/components/AuthedImage";
import { TYPOLOGIES } from "@/lib/calc";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Loader2, Library } from "lucide-react";

// Reusable dialog to attach a reference drawing to a BOQ item.
// Upload -> saved to the shared drawing library AND attached.
// Library -> pick an existing library drawing.
export function DrawingPicker({ open, onOpenChange, defaultTypology = "Fixed", onSelect }) {
  const [library, setLibrary] = useState([]);
  const [loadingLib, setLoadingLib] = useState(false);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [typology, setTypology] = useState(defaultTypology);
  const [uploading, setUploading] = useState(false);

  const loadLibrary = async () => {
    setLoadingLib(true);
    try {
      const { data } = await api.get("/library/drawings");
      setLibrary(data);
    } catch (e) {
      setLibrary([]);
    } finally {
      setLoadingLib(false);
    }
  };

  useEffect(() => {
    if (open) {
      setTypology(defaultTypology);
      loadLibrary();
    }
  }, [open, defaultTypology]);

  const doUpload = async () => {
    if (!file || !name.trim()) {
      toast.error("Pick a file and enter a name");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name.trim());
    fd.append("typology", typology);
    try {
      const { data } = await api.post("/library/drawings", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Added to library & attached");
      onSelect(data.file_id, data.name);
      setFile(null);
      setName("");
      onOpenChange(false);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="drawing-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Attach Reference Drawing</DialogTitle>
          <DialogDescription>Import a new drawing to your library or pick an existing one.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="upload">
          <TabsList className="rounded-sm bg-slate-100">
            <TabsTrigger value="upload" className="rounded-sm" data-testid="picker-tab-upload">
              <Upload className="w-4 h-4 mr-1.5" /> Import New
            </TabsTrigger>
            <TabsTrigger value="library" className="rounded-sm" data-testid="picker-tab-library">
              <Library className="w-4 h-4 mr-1.5" /> From Library
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="pt-4 space-y-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-slate-400">File (image or PDF)</Label>
              <Input
                type="file"
                accept="image/*,application/pdf"
                data-testid="picker-file-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setFile(f || null);
                  if (f && !name) setName(f.name.replace(/\.[^.]+$/, ""));
                }}
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-slate-400">Drawing Name</Label>
                <Input
                  value={name}
                  data-testid="picker-name-input"
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. SD1 Sliding Elevation"
                  className="rounded-sm h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-slate-400">Typology Tag</Label>
                <Select value={typology} onValueChange={setTypology}>
                  <SelectTrigger className="h-9 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPOLOGIES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              onClick={doUpload}
              disabled={uploading}
              data-testid="picker-upload-button"
              className="rounded-sm bg-blue-600 hover:bg-blue-700 w-full"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              Import to Library & Attach
            </Button>
          </TabsContent>

          <TabsContent value="library" className="pt-4">
            {loadingLib ? (
              <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
            ) : library.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">Your library is empty. Import a drawing first.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-[340px] overflow-y-auto thin-scroll">
                {library.map((d) => (
                  <button
                    key={d.id}
                    data-testid={`picker-library-item-${d.id}`}
                    onClick={() => { onSelect(d.file_id, d.name); onOpenChange(false); }}
                    className="border border-slate-200 rounded-sm overflow-hidden hover:border-blue-500 hover:shadow-sm transition-colors text-left group"
                  >
                    <div className="h-24 bg-slate-50">
                      <AuthedImage fileId={d.file_id} filename={d.filename} className="w-full h-full object-contain" />
                    </div>
                    <div className="p-1.5 border-t border-slate-100">
                      <div className="text-[11px] font-medium text-slate-800 truncate">{d.name}</div>
                      <div className="text-[9px] text-slate-400 truncate">{d.typology}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
